import {definePreset} from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

/**
 * PrimeNG preset that renders PrimeNG components in the CXone Lyra theme.
 *
 * Lyra ships no PrimeNG preset, so this maps PrimeNG's design tokens onto the
 * Lyra tokens from @niceltd/sol-angular. Values reference the raw `--lyra-*`
 * variables rather than `--sol-*`: PrimeNG declares its `--p-*` variables on
 * `:root`, where the `--lyra-*` tokens live, while the Lyra values of `--sol-*`
 * only exist from `<body data-theme="lyra">` down.
 */
const lyra = (token: string) => `var(--lyra-${token})`;

const brandScale = Object.fromEntries(
  [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950].map(step => [step, lyra(`base-color-brand-${step}`)])
);

const slateScale = Object.fromEntries(
  [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950].map(step => [step, lyra(`base-color-slate-${step}`)])
);

export const EspLyraPreset = definePreset(Lara, {
  primitive: {
    borderRadius: {
      none: lyra('radius-none'),
      xs: lyra('radius-xs'),
      sm: lyra('radius-sm'),
      md: lyra('radius-md'),
      lg: lyra('radius-lg'),
      xl: lyra('radius-xl')
    }
  },
  semantic: {
    primary: brandScale,
    focusRing: {
      width: '2px',
      style: 'solid',
      color: lyra('color-border-focus-default'),
      offset: '2px'
    },
    disabledOpacity: '1',
    formField: {
      paddingX: lyra('spacing-3'),
      paddingY: '0.375rem',
      borderRadius: '{border.radius.md}'
    },
    list: {
      padding: lyra('spacing-1'),
      option: {
        padding: `${lyra('spacing-2')} ${lyra('spacing-3')}`,
        borderRadius: lyra('radius-sm')
      }
    },
    overlay: {
      select: {
        borderRadius: lyra('radius-md'),
        shadow: lyra('shadow-lg')
      },
      popover: {
        borderRadius: lyra('radius-md'),
        shadow: lyra('shadow-lg')
      },
      modal: {
        borderRadius: lyra('radius-lg'),
        shadow: lyra('shadow-xl')
      },
      navigation: {
        shadow: lyra('shadow-lg')
      }
    },
    colorScheme: {
      light: {
        surface: {0: lyra('base-color-white'), ...slateScale},
        primary: {
          color: lyra('color-bg-primary'),
          contrastColor: lyra('color-fg-on-primary'),
          hoverColor: lyra('color-state-bg-hover-primary'),
          activeColor: lyra('color-state-bg-pressed-primary')
        },
        highlight: {
          background: lyra('color-bg-active-subtle'),
          focusBackground: lyra('color-state-bg-hover-active-subtle'),
          color: lyra('color-fg-active-strong'),
          focusColor: lyra('color-fg-active-strong')
        },
        focusRing: {
          shadow: 'none'
        },
        mask: {
          background: lyra('color-bg-surface-backdrop'),
          color: lyra('color-fg-inverse')
        },
        formField: {
          background: lyra('color-bg-field'),
          disabledBackground: lyra('color-bg-disabled'),
          filledBackground: lyra('color-bg-field'),
          filledHoverBackground: lyra('color-bg-field'),
          filledFocusBackground: lyra('color-bg-field'),
          borderColor: lyra('color-border-medium'),
          hoverBorderColor: lyra('color-border-strong'),
          focusBorderColor: lyra('color-border-focus-default'),
          invalidBorderColor: lyra('color-status-critical-strong'),
          color: lyra('color-fg-default'),
          disabledColor: lyra('color-fg-disabled'),
          placeholderColor: lyra('color-fg-secondary'),
          invalidPlaceholderColor: lyra('color-status-critical-strong'),
          floatLabelColor: lyra('color-fg-secondary'),
          floatLabelFocusColor: lyra('color-fg-action'),
          floatLabelActiveColor: lyra('color-fg-secondary'),
          iconColor: lyra('color-fg-secondary'),
          shadow: 'none'
        },
        text: {
          color: lyra('color-fg-default'),
          hoverColor: lyra('color-fg-default'),
          mutedColor: lyra('color-fg-secondary'),
          hoverMutedColor: lyra('color-fg-default')
        },
        content: {
          background: lyra('color-bg-surface-container'),
          hoverBackground: lyra('color-state-bg-hover-opacity'),
          borderColor: lyra('color-border-subtle'),
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: lyra('color-bg-surface-overlay'),
            borderColor: lyra('color-border-subtle'),
            color: '{text.color}'
          },
          popover: {
            background: lyra('color-bg-surface-overlay'),
            borderColor: lyra('color-border-subtle'),
            color: '{text.color}'
          },
          modal: {
            background: lyra('color-bg-surface-overlay'),
            borderColor: lyra('color-border-subtle'),
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: lyra('color-state-bg-hover-opacity'),
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: lyra('color-fg-secondary'),
              focusColor: lyra('color-fg-default')
            }
          },
          optionGroup: {
            background: 'transparent',
            color: lyra('color-fg-secondary')
          }
        },
        navigation: {
          item: {
            focusBackground: lyra('color-state-bg-hover-opacity'),
            activeBackground: lyra('color-bg-active-subtle'),
            color: '{text.color}',
            focusColor: '{text.color}',
            activeColor: lyra('color-fg-active-strong'),
            icon: {
              color: lyra('color-fg-secondary'),
              focusColor: lyra('color-fg-default'),
              activeColor: lyra('color-fg-active-strong')
            }
          }
        }
      }
    }
  },
  components: {
    ...formControlComponents()
  }
});

/**
 * Component tokens for the form controls, overlays and feedback components
 * (multiselect, select, inputtext, datepicker, tooltip, toast, toggleswitch).
 * They replace ESP's former global `.p-*` overrides in src/assets/styles.
 */
function formControlComponents() {
  // Status toast: white surface, status-coloured left accent and icon.
  const toastSeverity = (status: string) => ({
    background: lyra('color-bg-surface-overlay'),
    borderColor: lyra(`color-status-${status}-strong`),
    color: lyra(`color-status-${status}-strong`),
    detailColor: lyra('color-fg-default'),
    shadow: lyra('shadow-lg'),
    closeButton: {
      hoverBackground: lyra('color-state-bg-hover-opacity'),
      focusRing: {color: lyra('color-border-focus-default'), shadow: 'none'}
    }
  });

  return {
    // Field borders: stock Lyra border-medium at rest, border-strong on hover
    // (product decision - Lyra's field border over the darker border-strong).
    // Applied to every ESP field type so inputs, selects and date pickers
    // match side by side.
    inputtext: {
      root: {borderColor: lyra('color-border-medium'), hoverBorderColor: lyra('color-border-strong')}
    },
    select: {
      root: {borderColor: lyra('color-border-medium'), hoverBorderColor: lyra('color-border-strong')},
      dropdown: {width: lyra('control-height-md')}
    },
    multiselect: {
      root: {borderColor: lyra('color-border-medium'), hoverBorderColor: lyra('color-border-strong')},
      dropdown: {width: lyra('control-height-md')},
      // ESP option templates draw their own checkbox to show selection, so a
      // selected row gets no fill of its own - only the focus fill.
      option: {
        selectedBackground: 'transparent',
        selectedColor: '{list.option.color}',
        selectedFocusBackground: '{list.option.focus.background}',
        selectedFocusColor: '{list.option.focus.color}'
      }
    },
    datepicker: {
      panel: {
        borderRadius: '{overlay.popover.border.radius}'
      },
      title: {fontWeight: '600'},
      dropdown: {
        width: lyra('control-height-md'),
        // Matches the inputtext border beside it.
        borderColor: lyra('color-border-medium'),
        hoverBorderColor: lyra('color-border-strong'),
        activeBorderColor: lyra('color-border-strong')
      },
      weekDay: {
        padding: lyra('spacing-1'),
        fontWeight: '500',
        color: lyra('color-fg-secondary')
      },
      date: {
        width: lyra('control-height-md'),
        height: lyra('control-height-md'),
        padding: lyra('spacing-05'),
        borderRadius: lyra('radius-md')
      },
      colorScheme: {
        light: {
          dropdown: {
            background: lyra('color-bg-field'),
            hoverBackground: lyra('color-state-bg-hover-opacity'),
            activeBackground: lyra('color-state-bg-pressed-opacity'),
            color: lyra('color-fg-secondary'),
            hoverColor: lyra('color-fg-default'),
            activeColor: lyra('color-fg-default')
          },
          today: {
            background: 'transparent',
            color: lyra('color-fg-action')
          }
        }
      }
    },
    tooltip: {
      root: {
        maxWidth: '35em',
        gutter: lyra('spacing-1'),
        shadow: lyra('shadow-md'),
        padding: `${lyra('spacing-1')} ${lyra('spacing-2')}`,
        borderRadius: lyra('radius-sm')
      },
      colorScheme: {
        light: {
          root: {
            background: lyra('color-bg-surface-inverse'),
            color: lyra('color-fg-inverse')
          }
        }
      }
    },
    toast: {
      root: {
        width: '22rem',
        borderRadius: lyra('radius-lg'),
        borderWidth: `0 0 0 ${lyra('spacing-1')}`
      },
      colorScheme: {
        light: {
          info: toastSeverity('info'),
          success: toastSeverity('success'),
          warn: toastSeverity('warning'),
          error: toastSeverity('critical')
        }
      }
    },
    toggleswitch: {
      root: {
        width: lyra('spacing-9'),
        height: lyra('control-height-xs'),
        borderRadius: lyra('radius-round'),
        gap: lyra('spacing-05')
      },
      handle: {
        size: lyra('icon-size-sm'),
        borderRadius: lyra('radius-round')
      },
      colorScheme: {
        light: {
          // Unchecked track keeps >= 3:1 against the surface (WCAG 1.4.11).
          root: {
            background: lyra('color-border-strong'),
            hoverBackground: lyra('color-fg-secondary'),
            disabledBackground: lyra('color-bg-disabled'),
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.hover.color}'
          }
        }
      }
    }
  };
}
